// src/Pages/conges/TraiterConge.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { API_URL } from '../Config';
import { 
  ArrowLeft, 
  User, 
  Calendar, 
  Clock, 
  FileText, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Mail,
  Info,
  Shield
} from 'lucide-react';

const TraiterConge = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [conge, setConge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [soldeInfo, setSoldeInfo] = useState({ normal: 0, exceptionnel: 0 });
  
  const [formData, setFormData] = useState({
    action: 'validation',
    duree_accordee: '',
    justification: ''
  });

  useEffect(() => {
    fetchCongeDetails();
  }, [id]);

  const fetchCongeDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/conges/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Erreur chargement');
      
      const data = await response.json();
      setConge(data);
      
      const soldeResponse = await fetch(`${API_URL}/solde-conges/${data.user_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (soldeResponse.ok) {
        const soldeData = await soldeResponse.json();
        setSoldeInfo(soldeData);
      }
      
      setFormData(prev => ({
        ...prev,
        duree_accordee: data.duree || ''
      }));
      
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement');
      navigate('/validateur/conges');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRadioChange = (value) => {
    setFormData(prev => ({
      ...prev,
      action: value
    }));
  };

  const calculateSoldeRestant = () => {
    if (!conge || !soldeInfo) return null;
    
    const dureeDemandee = conge.duree;
    const dureeAccordee = parseInt(formData.duree_accordee) || dureeDemandee;
    const soldeInitial = conge.type === 'normal' ? soldeInfo.normal : soldeInfo.exceptionnel;
    
    return {
      soldeInitial,
      dureeDemandee,
      dureeAccordee,
      soldeRestant: soldeInitial - dureeAccordee
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const dureeAccordee = parseInt(formData.duree_accordee);
    if (!dureeAccordee || dureeAccordee < 0 || dureeAccordee > 22) {
      toast.error('Durée invalide (0-22 jours)');
      return;
    }
    
    if (!formData.justification.trim()) {
      toast.error('Justification obligatoire');
      return;
    }

    const solde = calculateSoldeRestant();
    if (formData.action === 'validation' && solde.soldeRestant < 0) {
      toast.error(`Solde insuffisant. Disponible: ${solde.soldeInitial} jours, Accordé: ${solde.dureeAccordee} jours`);
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/validateur/conges/${id}/confirmer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur ${response.status}`);
      }
      
      const result = await response.json();
      toast.success(result.message || 'Demande traitée avec succès');
      setTimeout(() => navigate('/validateur/conges'), 1000);
      
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.message || 'Erreur lors du traitement');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd MMM yyyy', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-3 text-gray-500 text-sm">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!conge) {
    return (
      <div className="min-h-screen  flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Demande non trouvée</h3>
          <p className="text-gray-500 text-sm mb-6">
            La demande de congé que vous recherchez n'existe pas ou a été supprimée.
          </p>
          <button
            onClick={() => navigate('/validateur/conges')}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user.role !== 'validateur') {
    return (
      <div className="min-h-screen  flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Shield className="w-16 h-16 text-red-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Accès refusé</h3>
          <p className="text-gray-500 text-sm mb-6">
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const solde = calculateSoldeRestant();

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/validateur/conges')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux demandes
          </button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Traiter la demande</h1>
            </div>
            <div className="inline-flex items-center px-3 py-1.5 bg-yellow-50 text-yellow-700 text-sm rounded-full border border-yellow-200">
              <AlertCircle className="w-4 h-4 mr-1.5" />
              À traiter
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Employee Info */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Employé</h2>
              </div>
              
              <div className="p-6">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{conge.user?.nom || '-'}</div>
                    {conge.user?.email && (
                      <div className="flex items-center text-sm text-gray-500 mt-1">
                        <Mail className="w-3 h-3 mr-2" />
                        {conge.user.email}
                      </div>
                    )}
                  </div>
                </div>

                {/* Balance */}
                <div className=" border border-gray-200 rounded p-4">
                  <label className="block text-sm text-gray-600 mb-3">Solde de congés</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className={`text-center p-3 rounded ${
                      conge.type === 'normal' ? 'bg-blue-50 border border-blue-100' : ' border border-gray-100'
                    }`}>
                      <div className="text-sm text-gray-600">Normaux</div>
                      <div className="text-lg font-semibold text-gray-900">{soldeInfo.normal}j</div>
                    </div>
                    <div className={`text-center p-3 rounded ${
                      conge.type === 'exceptionnel' ? 'bg-purple-50 border border-purple-100' : ' border border-gray-100'
                    }`}>
                      <div className="text-sm text-gray-600">Exceptionnels</div>
                      <div className="text-lg font-semibold text-gray-900">{soldeInfo.exceptionnel}j</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Leave Details */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Détails du congé</h2>
              </div>
              
              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Type</label>
                    <div className={`inline-flex px-3 py-1 text-sm rounded-full ${
                      conge.type === 'exceptionnel' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {conge.type === 'exceptionnel' ? 'Exceptionnel' : 'Normal'}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Durée demandée</label>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="font-medium text-gray-900">{conge.duree} jour{conge.duree > 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>

                {/* Period */}
                <div className="mb-6">
                  <label className="block text-sm text-gray-600 mb-3">Période</label>
                  <div className=" border border-gray-200 rounded p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">Début</div>
                        <div className="flex items-center mt-1">
                          <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="font-medium text-gray-900">{formatDate(conge.date_debut)}</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Fin</div>
                        <div className="flex items-center mt-1">
                          <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="font-medium text-gray-900">{formatDate(conge.date_fin)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Motif */}
                <div className="mb-6">
                  <label className="block text-sm text-gray-600 mb-3">Motif</label>
                  <div className=" border border-gray-200 rounded p-4">
                    <p className="text-gray-700">{conge.motif || '-'}</p>
                  </div>
                </div>

                {/* Exception Details */}
                {conge.exception_details && conge.exception_details.length > 0 && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-3">
                      Jours exceptionnels ({conge.exception_details.length})
                    </label>
                    <div className="space-y-3">
                      {conge.exception_details.map((detail, index) => (
                        <div key={index} className="border-l-2 border-purple-500 pl-4 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {format(new Date(detail.jour), 'dd MMMM yyyy', { locale: fr })}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">{detail.motif_jour}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Decision Panel */}
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Décision</h2>
              </div>
              
              <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Action Choice */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-3">Décision</label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className={`cursor-pointer border rounded-lg p-4 text-center transition-colors ${
                        formData.action === 'validation' 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}>
                        <input
                          type="radio"
                          name="action"
                          value="validation"
                          checked={formData.action === 'validation'}
                          onChange={(e) => handleRadioChange('validation')}
                          className="sr-only"
                        />
                        <CheckCircle className={`w-5 h-5 mx-auto mb-2 ${
                          formData.action === 'validation' ? 'text-green-600' : 'text-gray-400'
                        }`} />
                        <div className="text-sm font-medium">Valider</div>
                      </label>
                      
                      <label className={`cursor-pointer border rounded-lg p-4 text-center transition-colors ${
                        formData.action === 'refus' 
                          ? 'border-red-500 bg-red-50' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}>
                        <input
                          type="radio"
                          name="action"
                          value="refus"
                          checked={formData.action === 'refus'}
                          onChange={(e) => handleRadioChange('refus')}
                          className="sr-only"
                        />
                        <XCircle className={`w-5 h-5 mx-auto mb-2 ${
                          formData.action === 'refus' ? 'text-red-600' : 'text-gray-400'
                        }`} />
                        <div className="text-sm font-medium">Refuser</div>
                      </label>
                    </div>
                  </div>

                  {/* Duration */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">
                      Durée accordée (jours)
                    </label>
                    <input
                      type="number"
                      name="duree_accordee"
                      min="0"
                      max="22"
                      step="0.5"
                      value={formData.duree_accordee}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                      required
                    />
                    <div className="text-xs text-gray-500 mt-2">
                      Maximum 22 jours • Demandé: {conge.duree} jours
                    </div>
                  </div>

                  {/* Justification */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">
                      Justification
                    </label>
                    <textarea
                      name="justification"
                      value={formData.justification}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                      placeholder="Expliquez votre décision..."
                      required
                    />
                  </div>

                  {/* Balance Check */}
                  {solde && (
                    <div className={`p-4 rounded-lg ${
                      formData.action === 'validation' && solde.soldeRestant < 0 
                        ? 'bg-red-50 border border-red-200' 
                        : ' border border-gray-200'
                    }`}>
                      <label className="block text-sm text-gray-600 mb-3">Vérification solde</label>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Solde initial:</span>
                          <span className="font-medium text-gray-900">{solde.soldeInitial} jours</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Durée accordée:</span>
                          <span className="font-medium text-gray-900">{solde.dureeAccordee} jours</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Solde restant:</span>
                          <span className={`font-medium ${
                            solde.soldeRestant < 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {solde.soldeRestant} jours
                          </span>
                        </div>
                        {formData.action === 'validation' && solde.soldeRestant < 0 && (
                          <div className="text-xs text-red-600 mt-2 flex items-center">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Validation impossible: solde insuffisant
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="space-y-3">
                    <button
                      type="submit"
                      disabled={submitting || (formData.action === 'validation' && solde?.soldeRestant < 0)}
                      className={`w-full py-2.5 text-sm font-medium rounded ${
                        formData.action === 'validation'
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {submitting ? (
                        <span className="flex items-center justify-center">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Traitement...
                        </span>
                      ) : (
                        formData.action === 'validation' ? 'Valider' : 'Refuser'
                      )}
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => navigate('/validateur/conges')}
                      disabled={submitting}
                      className="w-full py-2.5 text-sm border border-gray-300 text-gray-700 rounded hover:"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Tips */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <Info className="w-4 h-4 mr-2" />
                  Conseils
                </h2>
              </div>
              
              <div className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-3"></div>
                    <span>Vérifiez la disponibilité de l'équipe</span>
                  </li>
                  <li className="flex items-start text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-3"></div>
                    <span>Vérifiez le solde de congés disponible</span>
                  </li>
                  <li className="flex items-start text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt=1.5 mr-3"></div>
                    <span>Justifiez clairement votre décision</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TraiterConge;